/**
 * BeatBandit design tokens — the single source of truth for the app's visual
 * language (warm near-black, single gold accent, mono-editable convention).
 *
 * Consumed two ways so there is never a second copy to keep in sync:
 *  1. `theme.ts` imports the constants for the MUI palette/typography/overrides.
 *  2. `injectBBTokens()` (called once in main.tsx) publishes them as CSS custom
 *     properties (`--bb-*`) so plain CSS and sx-strings can use them too.
 *
 * Theming: values live only here. A future light mode is a second token set
 * passed to `injectBBTokens()` plus an alternate MUI theme — no component edits.
 */

export const BB = {
  font: {
    display: '"Space Grotesk", system-ui, sans-serif',
    body: '"Inter", system-ui, sans-serif',
    mono: '"JetBrains Mono", ui-monospace, monospace',
    /* Screenplay text wherever it is quoted outside the editor (Agent excerpts, diffs). */
    script: '"Courier Prime", Courier, "Courier New", monospace',
  },

  /* surfaces */
  bg: '#0c0b0e', // app chrome (sidebar, panels)
  bgMain: '#0f0d12', // content area
  surface: '#15121a', // cards
  surface2: '#1a1622', // raised / hover / tooltips

  /* Writing workspaces only; global site chrome keeps its existing palette. */
  writing: {
    margin: '#131a28',
    page: '#252e42',
    border: '#657088',
    toolbar: '#fafaf7',
    toolbarInk: '#242c3a',
    toolbarLine: '#c9cdd4',
    toolbarHover: '#e9ecf1',
  },

  /* text */
  ink: '#f4f1ea', // primary
  inkDim: '#b7b2a8', // secondary / read-only prose
  inkFaint: '#7f7a72', // tertiary / labels
  inkEdit: '#faf7ef', // user-authored text inside editable fields — the brightest

  /* accent — one gold, used with discipline */
  gold: '#d4a853',
  goldDim: 'rgba(212, 168, 83, 0.55)',
  goldWash: 'rgba(212, 168, 83, 0.07)',
  onGold: '#1a1408',

  /* the writer's own voice — the only blue in the app; Agent user turns + composer */
  voice: '#9ec5ea',
  voiceDim: 'rgba(158, 197, 234, 0.55)',
  voiceWash: 'rgba(158, 197, 234, 0.12)',

  /* lines */
  line: 'rgba(244, 241, 234, 0.09)',
  line2: 'rgba(244, 241, 234, 0.16)',

  /* editable fields — THE signal for "you can write here" */
  fieldBorder: 'rgba(244, 241, 234, 0.12)',
  fieldBorderHover: 'rgba(212, 168, 83, 0.45)',
  fieldFocusRing: 'rgba(212, 168, 83, 0.14)',

  shadowCard: '0 1px 0 rgba(0, 0, 0, 0.2)',
} as const;

/**
 * Per-template accent hues — project-type identity color (dashboard cards etc.).
 * These are semantic content colors (like error-red), allowed alongside the single
 * brand gold. Hues carried over from the old sticky-note dashboard so returning
 * users keep their color memory: movie=amber, short=orange, tv=blue, micro=pink.
 */
export const BB_TEMPLATE_ACCENTS: Record<string, string> = {
  movie: '#e8c46a',
  genre_movies: '#b48fe8',
  short_movie: '#f0975e',
  one_scene: '#f4b183',
  advertisement: '#5fd0c2',
  tv: '#7fa8e8',
  tv_60: '#7fa8e8',
  tv_season: '#89b4f0',
  micro_drama: '#e887b0',
  single_scene: '#8fd0a8',
};

/** Accent + derived dim border / faint surface wash for a project template mode. */
export function templateAccent(mode?: string): { accent: string; dim: string; wash: string } {
  const hex = (mode && BB_TEMPLATE_ACCENTS[mode]) || BB.gold;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return {
    accent: hex,
    dim: `rgba(${r}, ${g}, ${b}, 0.45)`,
    wash: `rgba(${r}, ${g}, ${b}, 0.05)`,
  };
}

/** Kebab-case CSS custom properties derived from BB. */
export const BB_CSS_VARS: Record<string, string> = {
  '--bb-font-display': BB.font.display,
  '--bb-font-body': BB.font.body,
  '--bb-font-mono': BB.font.mono,
  '--bb-font-script': BB.font.script,
  '--bb-bg': BB.bg,
  '--bb-bg-main': BB.bgMain,
  '--bb-surface': BB.surface,
  '--bb-surface-2': BB.surface2,
  '--bb-ink': BB.ink,
  '--bb-ink-dim': BB.inkDim,
  '--bb-ink-faint': BB.inkFaint,
  '--bb-ink-edit': BB.inkEdit,
  '--bb-gold': BB.gold,
  '--bb-gold-dim': BB.goldDim,
  '--bb-gold-wash': BB.goldWash,
  '--bb-on-gold': BB.onGold,
  '--bb-voice': BB.voice,
  '--bb-voice-dim': BB.voiceDim,
  '--bb-voice-wash': BB.voiceWash,
  '--bb-line': BB.line,
  '--bb-line-2': BB.line2,
  '--bb-field-border': BB.fieldBorder,
  '--bb-field-border-hover': BB.fieldBorderHover,
  '--bb-field-focus-ring': BB.fieldFocusRing,
  '--bb-shadow-card': BB.shadowCard,
};

/** Publish tokens as CSS variables on :root. Call once before first render. */
export function injectBBTokens(root: HTMLElement = document.documentElement): void {
  for (const [name, value] of Object.entries(BB_CSS_VARS)) {
    root.style.setProperty(name, value);
  }
}
